import { useState } from 'react';
import Calendar from 'react-calendar';
import { RevenueDateRange } from 'types/revenue';
import { MdCalendarToday } from 'react-icons/md';
import 'react-calendar/dist/Calendar.css';

interface DateRangePickerProps {
  dateRange: RevenueDateRange;
  onDateRangeChange: (dateRange: RevenueDateRange) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateRange,
  onDateRangeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [calendarValue, setCalendarValue] = useState<
    Date | [Date, Date] | null
  >(
    dateRange.startDate && dateRange.endDate
      ? [dateRange.startDate, dateRange.endDate]
      : null
  );

  const handleDateChange = (value: Date | Date[] | null) => {
    // Convert Date[] to [Date, Date] if it's a valid range
    if (Array.isArray(value) && value.length === 2) {
      const rangeValue: [Date, Date] = [value[0], value[1]];
      setCalendarValue(rangeValue);
    } else if (value instanceof Date) {
      setCalendarValue(value);
    } else {
      setCalendarValue(null);
    }

    if (Array.isArray(value) && value.length === 2) {
      onDateRangeChange({
        startDate: value[0],
        endDate: value[1],
      });
      setIsOpen(false);
    } else if (value instanceof Date) {
      onDateRangeChange({
        startDate: value,
        endDate: value,
      });
      setIsOpen(false);
    }
  };

  const formatDateRange = (): string => {
    if (!dateRange.startDate && !dateRange.endDate) {
      return 'Select date range';
    }

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    if (dateRange.startDate && dateRange.endDate) {
      if (dateRange.startDate.getTime() === dateRange.endDate.getTime()) {
        return formatDate(dateRange.startDate);
      }
      return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;
    }

    return dateRange.startDate
      ? formatDate(dateRange.startDate)
      : dateRange.endDate
        ? formatDate(dateRange.endDate)
        : 'Select date range';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <MdCalendarToday className="h-4 w-4 text-gray-500" />
        <span className="text-sm text-gray-700">{formatDateRange()}</span>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 z-50 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="p-4">
            <Calendar
              onChange={handleDateChange}
              value={calendarValue}
              selectRange={true}
              className="react-calendar-custom"
            />
          </div>
        </div>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
      )}

      <style>{`
        :global(.react-calendar-custom) {
          border: none;
          font-family: inherit;
        }

        :global(.react-calendar-custom .react-calendar__tile) {
          max-width: 100%;
          text-align: center;
          padding: 0.75em 0.5em;
          background: none;
          border: none;
        }

        :global(.react-calendar-custom .react-calendar__tile:enabled:hover) {
          background-color: #e6f3ff;
        }

        :global(.react-calendar-custom .react-calendar__tile--active) {
          background: #4f46e5;
          color: white;
        }

        :global(.react-calendar-custom .react-calendar__tile--rangeStart,
         .react-calendar-custom .react-calendar__tile--rangeEnd) {
          background: #4f46e5;
          color: white;
        }

        :global(.react-calendar-custom .react-calendar__tile--range) {
          background: #e0e7ff;
        }
      `}</style>
    </div>
  );
};

export default DateRangePicker;
